import { linkOptions, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { VesselMessage } from '../../../../types/VesselMessage';
import { Button } from '../../../cdl/Button/Button';
import { CenteredPagination } from '../../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../../../cdl/Text/Text';
import { queries } from '../../../common/api/queryKeys/queries';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useBasicPaginationQuery } from '../../../common/hooks/useBasicPaginationQuery';
import { useRole } from '../../../common/hooks/useRole';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { OfferStatusTag } from '../../../common/OfferStatusTag/OfferStatusTag';
import { TableEmptyState } from '../../../common/TableEmptyState/TableEmptyState';
import { Box } from '../../../common/ui/Box';
import { OrderModel } from '../../../order/model/OrderModel';
import { Port } from '../../../port/Port';

interface VesselOrderTableProps {
    vessel: VesselMessage;
}

export const VesselOrderTable = ({ vessel }: VesselOrderTableProps) => {
    const role = useRole();

    const [page, setPage] = useState(0);

    const vesselOrdersQuery = useBasicPaginationQuery<OrderModel>({
        queryByPage: (page) =>
            queries.vessels.detail(vessel.id)._ctx.orders({
                vesselId: vessel.id,
                page,
                sortField: 'dateDelivery',
                sortDirection: 'DESC',
            }),
        page,
    });

    const navigate = useNavigate();

    const buildRowLink = (order: OrderModel) => {
        if (order.isClosed() && order.chosenOfferId) {
            return linkOptions({
                to: '/offer/$id',
                params: {
                    id: order.chosenOfferId,
                },
            });
        } else if (order.isDraft() && !role.isSuperAdmin() && role.hasWriteRights(order.customerId)) {
            return linkOptions({
                to: '/orders/draft/$id',
                params: {
                    id: order.id,
                },
            });
        } else {
            return linkOptions({
                to: '/order/$id',
                params: {
                    id: order.id,
                },
            });
        }
    };

    if (vesselOrdersQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (vesselOrdersQuery.isError) {
        return <AdvancedError />;
    }

    if (!vesselOrdersQuery.data.content.length) {
        return <TableEmptyState text={translate('order.emptylist')} />;
    }

    const navigateToOrdersPage = () => {
        navigate({ to: '/orders', search: { searchQuery: vessel.imo } });
    };

    return (
        <div>
            <Box display="flex" justifyContent="space-between" marginBottom={16}>
                <Text variant="title" as="h2">
                    {translate('order.headline')}
                </Text>
                <Button onClick={navigateToOrdersPage}>{translate('order.showAll')}</Button>
            </Box>

            <TableBuilder data={vesselOrdersQuery.data.content} rowLink={(order) => buildRowLink(order)} grow={false}>
                <TableBuilderColumn header={translate('order.buyerreferenceshort')} width="30%" truncate>
                    {(order: OrderModel) => order.buyerReference || Characters.EN_DASH}
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('order.port')} width="40%">
                    {(order: OrderModel) =>
                        order.port ? <Port port={order.port} vesselId={order.vesselId} showTooltip /> : null
                    }
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('order.dateDeliveryShort')} width="20%">
                    {(order: OrderModel) =>
                        order.dateDelivery ? formatDate({ date: order.dateDelivery, timeZone: 'UTC' }) : null
                    }
                </TableBuilderColumn>
                <TableBuilderColumn header={translate('order.status')} width="10%">
                    {(order: OrderModel) =>
                        order.offerState ? <OfferStatusTag state={order.offerState} variant="customer" /> : null
                    }
                </TableBuilderColumn>
            </TableBuilder>

            <CenteredPagination
                pageTotal={vesselOrdersQuery.pageTotal}
                currentPage={page}
                onPageChange={(page) => {
                    setPage(page);
                }}
            />
        </div>
    );
};
